import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { formatCurrency } from "../lib/utils";
import USDHint from "../components/USDHint";
import toast from "react-hot-toast";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingCart,
  Truck,
  AlertTriangle,
  Calendar,
  CalendarDays,
  Package,
  Printer,
  Share2,
  FileDown,
  FileSpreadsheet,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Users,
} from "lucide-react";
import {
  PrintShareButtons,
  generateReportId,
  printReport,
  shareReport,
  formatReportContent,
  generateReportSummary,
} from "../utils/printUtils";
import { exportReportToPDF } from "../utils/pdfExport";

const API = `${window.location.origin}/api`;

export default function ReportsPage() {
  const { } = useAuth();
  const language = "ar";
  const { t } = useTranslation(language);
  const [reportType, setReportType] = useState("daily");
  const [report, setReport] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [inventorySummary, setInventorySummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shopInfo, setShopInfo] = useState({});
  
  // Filter states
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  
  // Comparison states
  const [comparisonReport, setComparisonReport] = useState(null);
  const [comparisonType, setComparisonType] = useState("month");
  const [period1Month, setPeriod1Month] = useState(new Date().getMonth() + 1);
  const [period1Year, setPeriod1Year] = useState(new Date().getFullYear());
  const [period2Month, setPeriod2Month] = useState(new Date().getMonth());
  const [period2Year, setPeriod2Year] = useState(new Date().getFullYear());
  const [comparisonLoading, setComparisonLoading] = useState(false);

  useEffect(() => {
    fetchInventorySummary();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setShopInfo(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const fetchInventorySummary = async () => {
    try {
      const response = await axios.get(`${API}/reports/inventory-summary`);
      setInventorySummary(response.data);
    } catch (error) {
      console.error("Error fetching inventory summary:", error);
    }
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      let url = "";
      switch (reportType) {
        case "daily":
          url = `${API}/reports/daily?date=${selectedDate}`;
          break;
        case "monthly":
          url = `${API}/reports/monthly?year=${selectedYear}&month=${selectedMonth}`;
          break;
        case "quarterly":
          url = `${API}/reports/quarterly?year=${selectedYear}&quarter=${selectedQuarter}`;
          break;
        case "yearly":
          url = `${API}/reports/yearly?year=${selectedYear}`;
          break;
        default:
          url = `${API}/reports/daily`;
      }
      
      const response = await axios.get(url);
      setReport(response.data);
      setReportId(generateReportId());
    } catch (error) {
      toast.error(t("error"));
    } finally {
      setLoading(false);
    }
  };

  // دالة توليد تقرير المقارنة
  const generateComparisonReport = async () => {
    setComparisonLoading(true);
    try {
      // جلب بيانات الفترة الأولى
      const url1 = comparisonType === "month"
        ? `${API}/reports/monthly?year=${period1Year}&month=${period1Month}`
        : `${API}/reports/yearly?year=${period1Year}`;
      
      // جلب بيانات الفترة الثانية
      const url2 = comparisonType === "month"
        ? `${API}/reports/monthly?year=${period2Year}&month=${period2Month}`
        : `${API}/reports/yearly?year=${period2Year}`;
      
      const [res1, res2] = await Promise.all([
        axios.get(url1),
        axios.get(url2)
      ]);
      
      const period1 = res1.data;
      const period2 = res2.data;
      
      // حساب الفروقات والنسب
      const salesDiff = (period1.sales?.total || 0) - (period2.sales?.total || 0);
      const salesPercent = period2.sales?.total ? ((salesDiff / period2.sales.total) * 100).toFixed(1) : 0;
      
      const purchasesDiff = (period1.purchases?.total || 0) - (period2.purchases?.total || 0);
      const purchasesPercent = period2.purchases?.total ? ((purchasesDiff / period2.purchases.total) * 100).toFixed(1) : 0;
      
      const expensesDiff = (period1.expenses?.total || 0) - (period2.expenses?.total || 0);
      const expensesPercent = period2.expenses?.total ? ((expensesDiff / period2.expenses.total) * 100).toFixed(1) : 0;
      
      const profitDiff = (period1.profit?.net || 0) - (period2.profit?.net || 0);
      const profitPercent = period2.profit?.net ? ((profitDiff / period2.profit.net) * 100).toFixed(1) : 0;
      
      setComparisonReport({
        period1: {
          label: comparisonType === "month" 
            ? `${months[period1Month - 1]} ${period1Year}` 
            : `${period1Year}`,
          data: period1
        },
        period2: {
          label: comparisonType === "month" 
            ? `${months[period2Month - 1]} ${period2Year}` 
            : `${period2Year}`,
          data: period2
        },
        comparison: {
          sales: { diff: salesDiff, percent: salesPercent },
          purchases: { diff: purchasesDiff, percent: purchasesPercent },
          expenses: { diff: expensesDiff, percent: expensesPercent },
          profit: { diff: profitDiff, percent: profitPercent },
        }
      });
      
    } catch (error) {
      console.error("Comparison error:", error);
      toast.error(language === "ar" ? "خطأ في جلب بيانات المقارنة" : "Error fetching comparison data");
    } finally {
      setComparisonLoading(false);
    }
  };

  // دالة طباعة التقرير
  const handlePrintReport = () => {
    if (!report || !reportId) return;
    
    const reportTypeNames = {
      daily: language === "ar" ? "التقرير اليومي" : "Daily Report",
      monthly: language === "ar" ? "التقرير الشهري" : "Monthly Report",
      quarterly: language === "ar" ? "التقرير الربع سنوي" : "Quarterly Report",
      yearly: language === "ar" ? "التقرير السنوي" : "Yearly Report",
    };
    
    const title = `${reportTypeNames[reportType]} - ${report.period || report.date || report.year}`;
    const content = formatReportContent(report, reportType, language);
    printReport(title, content, reportId, { name: shopInfo.shop_name, phone: shopInfo.phone });
  };

  // دالة مشاركة التقرير
  const handleShareReport = () => {
    if (!report || !reportId) return;
    
    const reportTypeNames = {
      daily: language === "ar" ? "التقرير اليومي" : "Daily Report",
      monthly: language === "ar" ? "التقرير الشهري" : "Monthly Report",
      quarterly: language === "ar" ? "التقرير الربع سنوي" : "Quarterly Report",
      yearly: language === "ar" ? "التقرير السنوي" : "Yearly Report",
    };
    
    const title = `${reportTypeNames[reportType]} - ${report.period || report.date || report.year}`;
    const summary = generateReportSummary(report, language);
    shareReport(title, summary, reportId, language);
  };

  // دالة تصدير PDF
  const handleExportPDF = async () => {
    if (!report || !reportId) return;
    
    try {
      await exportReportToPDF(report, reportType, reportId, language, shopInfo);
      toast.success(language === "ar" ? "تم تصدير التقرير كـ PDF" : "Report exported as PDF");
    } catch (error) {
      console.error("PDF Export error:", error);
      toast.error(language === "ar" ? "فشل تصدير PDF" : "PDF export failed");
    }
  };

  // دالة تصدير Excel محسنة مع جميع التفاصيل المالية
  const handleExportExcel = () => {
    if (!report) return;
    
    try {
      const reportTypeNames = {
        daily: language === "ar" ? "التقرير_اليومي" : "Daily_Report",
        monthly: language === "ar" ? "التقرير_الشهري" : "Monthly_Report",
        quarterly: language === "ar" ? "التقرير_الربع_سنوي" : "Quarterly_Report",
        yearly: language === "ar" ? "التقرير_السنوي" : "Yearly_Report",
      };

      // معلومات المؤسسة
      const shopHeader = [
        [shopInfo.shop_name || (language === "ar" ? "اسم المؤسسة" : "Shop Name")],
        [shopInfo.phone ? (language === "ar" ? `هاتف: ${shopInfo.phone}` : `Phone: ${shopInfo.phone}`) : ""],
        [],
        [language === "ar" ? "═══════════════════════════════════════" : "═══════════════════════════════════════"],
        [reportTypeNames[reportType].replace(/_/g, " ")],
        [language === "ar" ? `الفترة: ${report.period || report.date || report.year}` : `Period: ${report.period || report.date || report.year}`],
        [language === "ar" ? `رقم التقرير: ${reportId}` : `Report ID: ${reportId}`],
        [language === "ar" ? `تاريخ الإصدار: ${new Date().toLocaleDateString('ar-IQ')}` : `Date: ${new Date().toLocaleDateString()}`],
        [],
      ];

      // ملخص التقرير المالي الشامل
      const summaryData = [
        ...shopHeader,
        [language === "ar" ? "═══════════ الملخص المالي ═══════════" : "═══════════ Financial Summary ═══════════"],
        [],
        [language === "ar" ? "#" : "#", language === "ar" ? "البند" : "Item", language === "ar" ? "المبلغ (د.ع)" : "Amount (IQD)", language === "ar" ? "العدد" : "Count"],
        [1, language === "ar" ? "إجمالي المبيعات" : "Total Sales", report.sales?.total || 0, report.sales?.count || 0],
        [2, language === "ar" ? "- المبيعات النقدية" : "  - Cash Sales", report.sales?.cash || 0, "-"],
        [3, language === "ar" ? "- المبيعات الآجلة" : "  - Credit Sales", report.sales?.credit || 0, "-"],
        [],
        [4, language === "ar" ? "إجمالي المشتريات" : "Total Purchases", report.purchases?.total || 0, report.purchases?.count || 0],
        [5, language === "ar" ? "إجمالي المصاريف" : "Total Expenses", report.expenses?.total || 0, report.expenses?.count || 0],
        [6, language === "ar" ? "إجمالي التلفيات" : "Total Damages", report.damages?.total || 0, report.damages?.count || 0],
        [],
        [language === "ar" ? "═══════════════════════════════════════" : "═══════════════════════════════════════"],
        [7, language === "ar" ? "إجمالي الربح (المبيعات - المشتريات)" : "Gross Profit (Sales - Purchases)", 
          (report.sales?.total || 0) - (report.purchases?.total || 0), ""],
        [8, language === "ar" ? "صافي الربح / الخسارة" : "Net Profit / Loss", report.net_profit || 0, ""],
        [],
      ];

      // إضافة تفاصيل الرواتب إن وجدت
      if (report.salaries && report.salaries.total > 0) {
        summaryData.push(
          [9, language === "ar" ? "الرواتب المدفوعة" : "Salaries Paid", report.salaries.total, report.salaries.count || 0]
        );
      }

      // إعداد بيانات المبيعات اليومية/الشهرية
      let salesData = [];
      if (report.sales_by_day && report.sales_by_day.length > 0) {
        salesData = [
          [language === "ar" ? "═══════════ المبيعات اليومية ═══════════" : "═══════════ Daily Sales ═══════════"],
          [],
          [language === "ar" ? "#" : "#", language === "ar" ? "التاريخ" : "Date", language === "ar" ? "المبيعات (د.ع)" : "Sales (IQD)", language === "ar" ? "عدد الفواتير" : "Invoice Count"],
          ...report.sales_by_day.map((item, idx) => [idx + 1, item.date, item.sales, item.count])
        ];
      } else if (report.sales_by_month && report.sales_by_month.length > 0) {
        salesData = [
          [language === "ar" ? "═══════════ المبيعات الشهرية ═══════════" : "═══════════ Monthly Sales ═══════════"],
          [],
          [language === "ar" ? "#" : "#", language === "ar" ? "الشهر" : "Month", language === "ar" ? "المبيعات (د.ع)" : "Sales (IQD)", language === "ar" ? "عدد الفواتير" : "Invoice Count"],
          ...report.sales_by_month.map((item, idx) => [idx + 1, item.month, item.sales, item.count])
        ];
      }

      // إعداد بيانات المنتجات الأكثر مبيعاً
      let productsData = [];
      if (report.top_products && report.top_products.length > 0) {
        productsData = [
          [language === "ar" ? "═══════════ المنتجات الأكثر مبيعاً ═══════════" : "═══════════ Top Selling Products ═══════════"],
          [],
          [language === "ar" ? "#" : "#", language === "ar" ? "اسم المنتج" : "Product Name", language === "ar" ? "الكمية المباعة" : "Qty Sold", language === "ar" ? "الإيراد (د.ع)" : "Revenue (IQD)"],
          ...report.top_products.map((item, idx) => [idx + 1, item.name, item.quantity, item.revenue])
        ];
      }

      // إعداد البيانات الشهرية للتقرير السنوي
      let monthlyData = [];
      if (report.monthly_data) {
        monthlyData = [
          [language === "ar" ? "═══════════ التفاصيل الشهرية ═══════════" : "═══════════ Monthly Details ═══════════"],
          [],
          [language === "ar" ? "#" : "#", language === "ar" ? "الشهر" : "Month", language === "ar" ? "المبيعات" : "Sales", language === "ar" ? "المشتريات" : "Purchases", language === "ar" ? "المصاريف" : "Expenses", language === "ar" ? "الربح" : "Profit"],
          ...Object.entries(report.monthly_data).map(([month, data], idx) => [
            idx + 1, month, data.sales, data.purchases, data.expenses, data.profit
          ])
        ];
      }

      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Add summary sheet
      const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
      // تعيين عرض الأعمدة
      summaryWs['!cols'] = [
        {wch: 5},  // #
        {wch: 35}, // البند
        {wch: 18}, // المبلغ
        {wch: 10}, // العدد
      ];
      XLSX.utils.book_append_sheet(wb, summaryWs, language === "ar" ? "الملخص المالي" : "Financial Summary");
      
      // Add sales sheet if data exists
      if (salesData.length > 0) {
        const salesWs = XLSX.utils.aoa_to_sheet(salesData);
        salesWs['!cols'] = [{wch: 5}, {wch: 20}, {wch: 18}, {wch: 15}];
        XLSX.utils.book_append_sheet(wb, salesWs, language === "ar" ? "المبيعات" : "Sales");
      }
      
      // Add products sheet if data exists
      if (productsData.length > 0) {
        const productsWs = XLSX.utils.aoa_to_sheet(productsData);
        productsWs['!cols'] = [{wch: 5}, {wch: 30}, {wch: 15}, {wch: 18}];
        XLSX.utils.book_append_sheet(wb, productsWs, language === "ar" ? "المنتجات" : "Products");
      }

      // Add monthly data sheet for yearly report
      if (monthlyData.length > 0) {
        const monthlyWs = XLSX.utils.aoa_to_sheet(monthlyData);
        monthlyWs['!cols'] = [{wch: 5}, {wch: 15}, {wch: 15}, {wch: 15}, {wch: 15}, {wch: 15}];
        XLSX.utils.book_append_sheet(wb, monthlyWs, language === "ar" ? "التفاصيل الشهرية" : "Monthly");
      }

      // Generate filename with date and report ID
      const date = report.period || report.date || report.year || new Date().toISOString().split('T')[0];
      const filename = `${reportTypeNames[reportType]}_${date}_${reportId}.xlsx`;
      
      // Save file
      XLSX.writeFile(wb, filename);
      toast.success(language === "ar" ? "تم تصدير التقرير كـ Excel بنجاح" : "Report exported as Excel");
    } catch (error) {
      console.error("Excel Export error:", error);
      toast.error(language === "ar" ? "فشل تصدير Excel" : "Excel export failed");
    }
  };

  const months = language === "ar" 
    ? ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"]
    : ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const quarters = language === "ar"
    ? ["الربع الأول", "الربع الثاني", "الربع الثالث", "الربع الرابع"]
    : ["Q1", "Q2", "Q3", "Q4"];

  const years = Array.from({length: 5}, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold text-white">{t("reports")}</h1>
      </div>

      {/* Inventory Summary */}
      {inventorySummary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Package className="w-8 h-8 text-amber-500" />
                <div>
                  <p className="text-slate-400 text-xs">{language === "ar" ? "المنتجات" : "Products"}</p>
                  <p className="text-xl font-bold text-white">{inventorySummary.total_products}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Truck className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-slate-400 text-xs">{language === "ar" ? "قيمة الشراء" : "Purchase Value"}</p>
                  <p className="text-xl font-bold text-blue-500">{formatCurrency(inventorySummary.total_purchase_value)}</p>
                  <USDHint amount={inventorySummary.total_purchase_value} />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <ShoppingCart className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-slate-400 text-xs">{language === "ar" ? "قيمة البيع" : "Sale Value"}</p>
                  <p className="text-xl font-bold text-green-500">{formatCurrency(inventorySummary.total_sale_value)}</p>
                  <USDHint amount={inventorySummary.total_sale_value} />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-emerald-500" />
                <div>
                  <p className="text-slate-400 text-xs">{language === "ar" ? "الربح المتوقع" : "Expected Profit"}</p>
                  <p className="text-xl font-bold text-emerald-500">{formatCurrency(inventorySummary.potential_profit)}</p>
                  <USDHint amount={inventorySummary.potential_profit} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Report Type Tabs */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-6">
          <Tabs value={reportType} onValueChange={setReportType} className="w-full">
            <TabsList className="grid w-full grid-cols-5 bg-slate-900 mb-6">
              <TabsTrigger value="daily" className="data-[state=active]:bg-red-600">
                <Calendar className="w-4 h-4 me-2" />
                {language === "ar" ? "يومي" : "Daily"}
              </TabsTrigger>
              <TabsTrigger value="monthly" className="data-[state=active]:bg-red-600">
                <CalendarDays className="w-4 h-4 me-2" />
                {language === "ar" ? "شهري" : "Monthly"}
              </TabsTrigger>
              <TabsTrigger value="quarterly" className="data-[state=active]:bg-red-600">
                <BarChart3 className="w-4 h-4 me-2" />
                {language === "ar" ? "ربع سنوي" : "Quarterly"}
              </TabsTrigger>
              <TabsTrigger value="yearly" className="data-[state=active]:bg-red-600">
                <TrendingUp className="w-4 h-4 me-2" />
                {language === "ar" ? "سنوي" : "Yearly"}
              </TabsTrigger>
              <TabsTrigger value="comparison" className="data-[state=active]:bg-purple-600">
                <ArrowUpDown className="w-4 h-4 me-2" />
                {language === "ar" ? "مقارنة" : "Compare"}
              </TabsTrigger>
            </TabsList>

            {/* Daily Filter */}
            <TabsContent value="daily">
              <div className="flex gap-4 items-end">
                <div className="flex-1 space-y-2">
                  <Label className="text-slate-200">{language === "ar" ? "التاريخ" : "Date"}</Label>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
                <Button onClick={generateReport} disabled={loading} className="bg-red-600 hover:bg-red-700">
                  {loading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : (language === "ar" ? "عرض التقرير" : "View Report")}
                </Button>
              </div>
            </TabsContent>

            {/* Monthly Filter */}
            <TabsContent value="monthly">
              <div className="flex gap-4 items-end">
                <div className="flex-1 space-y-2">
                  <Label className="text-slate-200">{language === "ar" ? "السنة" : "Year"}</Label>
                  <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {years.map((y) => (
                        <SelectItem key={y} value={y.toString()} className="text-white hover:bg-slate-700">{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 space-y-2">
                  <Label className="text-slate-200">{language === "ar" ? "الشهر" : "Month"}</Label>
                  <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {months.map((m, i) => (
                        <SelectItem key={i} value={(i + 1).toString()} className="text-white hover:bg-slate-700">{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={generateReport} disabled={loading} className="bg-red-600 hover:bg-red-700">
                  {loading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : (language === "ar" ? "عرض التقرير" : "View Report")}
                </Button>
              </div>
            </TabsContent>

            {/* Quarterly Filter */}
            <TabsContent value="quarterly">
              <div className="flex gap-4 items-end">
                <div className="flex-1 space-y-2">
                  <Label className="text-slate-200">{language === "ar" ? "السنة" : "Year"}</Label>
                  <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {years.map((y) => (
                        <SelectItem key={y} value={y.toString()} className="text-white hover:bg-slate-700">{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 space-y-2">
                  <Label className="text-slate-200">{language === "ar" ? "الربع" : "Quarter"}</Label>
                  <Select value={selectedQuarter.toString()} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {quarters.map((q, i) => (
                        <SelectItem key={i} value={(i + 1).toString()} className="text-white hover:bg-slate-700">{q}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={generateReport} disabled={loading} className="bg-red-600 hover:bg-red-700">
                  {loading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : (language === "ar" ? "عرض التقرير" : "View Report")}
                </Button>
              </div>
            </TabsContent>

            {/* Yearly Filter */}
            <TabsContent value="yearly">
              <div className="flex gap-4 items-end">
                <div className="flex-1 space-y-2">
                  <Label className="text-slate-200">{language === "ar" ? "السنة" : "Year"}</Label>
                  <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {years.map((y) => (
                        <SelectItem key={y} value={y.toString()} className="text-white hover:bg-slate-700">{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={generateReport} disabled={loading} className="bg-red-600 hover:bg-red-700">
                  {loading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : (language === "ar" ? "عرض التقرير" : "View Report")}
                </Button>
              </div>
            </TabsContent>

            {/* Comparison Filter */}
            <TabsContent value="comparison">
              <div className="space-y-4">
                {/* نوع المقارنة */}
                <div className="flex gap-4 items-center">
                  <Label className="text-slate-200 min-w-20">{language === "ar" ? "نوع المقارنة:" : "Compare by:"}</Label>
                  <Select value={comparisonType} onValueChange={setComparisonType}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="month" className="text-white hover:bg-slate-700">
                        {language === "ar" ? "شهري" : "Monthly"}
                      </SelectItem>
                      <SelectItem value="year" className="text-white hover:bg-slate-700">
                        {language === "ar" ? "سنوي" : "Yearly"}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* الفترتين للمقارنة */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* الفترة الأولى */}
                  <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                    <h4 className="text-emerald-400 font-medium mb-3 flex items-center gap-2">
                      <ArrowUp className="w-4 h-4" />
                      {language === "ar" ? "الفترة الأولى (الأحدث)" : "Period 1 (Recent)"}
                    </h4>
                    <div className="flex gap-3">
                      {comparisonType === "month" && (
                        <Select value={period1Month.toString()} onValueChange={(v) => setPeriod1Month(parseInt(v))}>
                          <SelectTrigger className="bg-slate-900 border-slate-700 text-white flex-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            {months.map((m, i) => (
                              <SelectItem key={i} value={(i + 1).toString()} className="text-white hover:bg-slate-700">{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Select value={period1Year.toString()} onValueChange={(v) => setPeriod1Year(parseInt(v))}>
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          {years.map((y) => (
                            <SelectItem key={y} value={y.toString()} className="text-white hover:bg-slate-700">{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* الفترة الثانية */}
                  <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <h4 className="text-amber-400 font-medium mb-3 flex items-center gap-2">
                      <ArrowDown className="w-4 h-4" />
                      {language === "ar" ? "الفترة الثانية (للمقارنة)" : "Period 2 (Compare to)"}
                    </h4>
                    <div className="flex gap-3">
                      {comparisonType === "month" && (
                        <Select value={period2Month.toString()} onValueChange={(v) => setPeriod2Month(parseInt(v))}>
                          <SelectTrigger className="bg-slate-900 border-slate-700 text-white flex-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            {months.map((m, i) => (
                              <SelectItem key={i} value={(i + 1).toString()} className="text-white hover:bg-slate-700">{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Select value={period2Year.toString()} onValueChange={(v) => setPeriod2Year(parseInt(v))}>
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          {years.map((y) => (
                            <SelectItem key={y} value={y.toString()} className="text-white hover:bg-slate-700">{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={generateComparisonReport} 
                  disabled={comparisonLoading} 
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  data-testid="generate-comparison-btn"
                >
                  {comparisonLoading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <ArrowUpDown className="w-4 h-4 me-2" />
                      {language === "ar" ? "عرض المقارنة" : "Show Comparison"}
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Comparison Results */}
      {comparisonReport && reportType === "comparison" && (
        <Card className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border-purple-500/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <ArrowUpDown className="w-5 h-5 text-purple-400" />
              {language === "ar" ? "نتيجة المقارنة" : "Comparison Results"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Period Labels */}
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <p className="text-emerald-400 font-bold">{comparisonReport.period1.label}</p>
                <p className="text-slate-400 text-xs">{language === "ar" ? "الفترة الأولى" : "Period 1"}</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                <p className="text-purple-400 font-bold">{language === "ar" ? "الفرق" : "Difference"}</p>
                <p className="text-slate-400 text-xs">{language === "ar" ? "التغيير" : "Change"}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <p className="text-amber-400 font-bold">{comparisonReport.period2.label}</p>
                <p className="text-slate-400 text-xs">{language === "ar" ? "الفترة الثانية" : "Period 2"}</p>
              </div>
            </div>

            {/* Comparison Grid */}
            <div className="space-y-4">
              {/* المبيعات */}
              <div className="grid grid-cols-3 gap-4 p-4 rounded-lg bg-slate-800/50">
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-400">{formatCurrency(comparisonReport.period1.data.sales?.total || 0)}</p>
                </div>
                <div className="text-center">
                  <p className={`text-lg font-bold ${comparisonReport.comparison.sales.diff >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {comparisonReport.comparison.sales.diff >= 0 ? '+' : ''}{formatCurrency(comparisonReport.comparison.sales.diff)}
                  </p>
                  <p className={`text-sm ${comparisonReport.comparison.sales.diff >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    ({comparisonReport.comparison.sales.diff >= 0 ? '+' : ''}{comparisonReport.comparison.sales.percent}%)
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-400">{formatCurrency(comparisonReport.period2.data.sales?.total || 0)}</p>
                </div>
                <div className="col-span-3 text-center pt-2 border-t border-slate-700">
                  <span className="text-slate-400 flex items-center justify-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    {language === "ar" ? "المبيعات" : "Sales"}
                  </span>
                </div>
              </div>

              {/* المشتريات */}
              <div className="grid grid-cols-3 gap-4 p-4 rounded-lg bg-slate-800/50">
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-400">{formatCurrency(comparisonReport.period1.data.purchases?.total || 0)}</p>
                </div>
                <div className="text-center">
                  <p className={`text-lg font-bold ${comparisonReport.comparison.purchases.diff <= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {comparisonReport.comparison.purchases.diff >= 0 ? '+' : ''}{formatCurrency(comparisonReport.comparison.purchases.diff)}
                  </p>
                  <p className={`text-sm ${comparisonReport.comparison.purchases.diff <= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    ({comparisonReport.comparison.purchases.diff >= 0 ? '+' : ''}{comparisonReport.comparison.purchases.percent}%)
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-400">{formatCurrency(comparisonReport.period2.data.purchases?.total || 0)}</p>
                </div>
                <div className="col-span-3 text-center pt-2 border-t border-slate-700">
                  <span className="text-slate-400 flex items-center justify-center gap-2">
                    <Truck className="w-4 h-4" />
                    {language === "ar" ? "المشتريات" : "Purchases"}
                  </span>
                </div>
              </div>

              {/* المصاريف */}
              <div className="grid grid-cols-3 gap-4 p-4 rounded-lg bg-slate-800/50">
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-400">{formatCurrency(comparisonReport.period1.data.expenses?.total || 0)}</p>
                </div>
                <div className="text-center">
                  <p className={`text-lg font-bold ${comparisonReport.comparison.expenses.diff <= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {comparisonReport.comparison.expenses.diff >= 0 ? '+' : ''}{formatCurrency(comparisonReport.comparison.expenses.diff)}
                  </p>
                  <p className={`text-sm ${comparisonReport.comparison.expenses.diff <= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    ({comparisonReport.comparison.expenses.diff >= 0 ? '+' : ''}{comparisonReport.comparison.expenses.percent}%)
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-400">{formatCurrency(comparisonReport.period2.data.expenses?.total || 0)}</p>
                </div>
                <div className="col-span-3 text-center pt-2 border-t border-slate-700">
                  <span className="text-slate-400 flex items-center justify-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {language === "ar" ? "المصاريف" : "Expenses"}
                  </span>
                </div>
              </div>

              {/* صافي الربح */}
              <div className="grid grid-cols-3 gap-4 p-4 rounded-lg bg-gradient-to-r from-emerald-500/20 to-purple-500/20 border border-emerald-500/30">
                <div className="text-center">
                  <p className="text-3xl font-bold text-emerald-400">{formatCurrency(comparisonReport.period1.data.profit?.net || 0)}</p>
                </div>
                <div className="text-center">
                  <p className={`text-xl font-bold ${comparisonReport.comparison.profit.diff >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {comparisonReport.comparison.profit.diff >= 0 ? '+' : ''}{formatCurrency(comparisonReport.comparison.profit.diff)}
                  </p>
                  <p className={`text-sm ${comparisonReport.comparison.profit.diff >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    ({comparisonReport.comparison.profit.diff >= 0 ? '+' : ''}{comparisonReport.comparison.profit.percent}%)
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-amber-400">{formatCurrency(comparisonReport.period2.data.profit?.net || 0)}</p>
                </div>
                <div className="col-span-3 text-center pt-2 border-t border-emerald-500/30">
                  <span className="text-white font-bold flex items-center justify-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    {language === "ar" ? "صافي الربح" : "Net Profit"}
                  </span>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="p-4 rounded-lg bg-slate-800/50 text-center">
              {comparisonReport.comparison.profit.diff >= 0 ? (
                <p className="text-emerald-400 font-medium">
                  <TrendingUp className="w-5 h-5 inline-block me-2" />
                  {language === "ar" 
                    ? `تحسّن الأداء بنسبة ${comparisonReport.comparison.profit.percent}%`
                    : `Performance improved by ${comparisonReport.comparison.profit.percent}%`}
                </p>
              ) : (
                <p className="text-red-400 font-medium">
                  <TrendingDown className="w-5 h-5 inline-block me-2" />
                  {language === "ar" 
                    ? `انخفض الأداء بنسبة ${Math.abs(comparisonReport.comparison.profit.percent)}%`
                    : `Performance decreased by ${Math.abs(comparisonReport.comparison.profit.percent)}%`}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Report Results */}
      {report && (
        <div className="space-y-6">
          {/* Period Header with Print/Share/PDF */}
          <Card className="bg-gradient-to-r from-red-600/20 to-amber-600/20 border-red-500/30">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="text-slate-300 text-sm">{language === "ar" ? "فترة التقرير" : "Report Period"}</p>
                  <p className="text-2xl font-bold text-white">{report.period || report.date || report.year}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <PrintShareButtons
                    reportId={reportId}
                    onPrint={handlePrintReport}
                    onShare={handleShareReport}
                    language={language}
                  />
                  <Button
                    size="sm"
                    onClick={handleExportPDF}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <FileDown className="w-4 h-4 me-1" />
                    {language === "ar" ? "PDF" : "PDF"}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleExportExcel}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="export-excel-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4 me-1" />
                    {language === "ar" ? "Excel" : "Excel"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-green-500/10">
                    <ShoppingCart className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">{language === "ar" ? "المبيعات" : "Sales"}</p>
                    <p className="text-xl font-bold text-green-500">{formatCurrency(report.sales?.total || 0)}</p>
                    <USDHint amount={report.sales?.total || 0} />
                    <p className="text-xs text-slate-500">{report.sales?.count || 0} {language === "ar" ? "عملية" : "transactions"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-red-500/10">
                    <Truck className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">{language === "ar" ? "المشتريات" : "Purchases"}</p>
                    <p className="text-xl font-bold text-red-500">{formatCurrency(report.purchases?.total || 0)}</p>
                    <p className="text-xs text-slate-500">{report.purchases?.count || 0} {language === "ar" ? "عملية" : "transactions"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-amber-500/10">
                    <DollarSign className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">{language === "ar" ? "المصاريف" : "Expenses"}</p>
                    <p className="text-xl font-bold text-amber-500">{formatCurrency(report.expenses?.total || 0)}</p>
                    <USDHint amount={report.expenses?.total || 0} />
                    <p className="text-xs text-slate-500">{report.expenses?.count || 0} {language === "ar" ? "مصروف" : "expenses"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-orange-500/10">
                    <AlertTriangle className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">{language === "ar" ? "التلفيات" : "Damages"}</p>
                    <p className="text-xl font-bold text-orange-500">{formatCurrency(report.damages?.total || 0)}</p>
                    <p className="text-xs text-slate-500">{report.damages?.count || 0} {language === "ar" ? "تلف" : "items"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Net Profit/Loss */}
          <Card className={`${(report.net_profit || 0) >= 0 ? "bg-green-500/10 border-green-500/30" : "bg-red-500/10 border-red-500/30"}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {(report.net_profit || 0) >= 0 ? (
                    <TrendingUp className="w-10 h-10 text-green-500" />
                  ) : (
                    <TrendingDown className="w-10 h-10 text-red-500" />
                  )}
                  <div>
                    <p className="text-slate-300 text-sm">
                      {(report.net_profit || 0) >= 0 
                        ? (language === "ar" ? "صافي الربح" : "Net Profit")
                        : (language === "ar" ? "صافي الخسارة" : "Net Loss")}
                    </p>
                    <p className={`text-3xl font-bold ${(report.net_profit || 0) >= 0 ? "text-green-500" : "text-red-500"}`}>
                      {formatCurrency(Math.abs(report.net_profit || 0))}
                    </p>
                    <USDHint amount={Math.abs(report.net_profit || 0)} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Salaries - if available */}
          {report.salaries && report.salaries.total > 0 && (
            <Card className="bg-purple-500/10 border-purple-500/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-purple-500/20">
                    <DollarSign className="w-6 h-6 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">{language === "ar" ? "الرواتب المدفوعة" : "Salaries Paid"}</p>
                    <p className="text-xl font-bold text-purple-500">{formatCurrency(report.salaries.total)}</p>
                    <p className="text-xs text-slate-500">{language === "ar" ? "تم خصمها من صافي الربح" : "Deducted from net profit"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sales Breakdown - if available */}
          {report.sales?.cash !== undefined && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">{language === "ar" ? "تفاصيل المبيعات" : "Sales Breakdown"}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-slate-900/50">
                    <p className="text-slate-400 text-sm">{language === "ar" ? "نقدي" : "Cash"}</p>
                    <p className="text-xl font-bold text-green-500">{formatCurrency(report.sales.cash || 0)}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-900/50">
                    <p className="text-slate-400 text-sm">{language === "ar" ? "آجل" : "Credit"}</p>
                    <p className="text-xl font-bold text-amber-500">{formatCurrency(report.sales.credit || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Monthly Data for Yearly Report */}
          {report.monthly_data && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">{language === "ar" ? "البيانات الشهرية" : "Monthly Data"}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-center p-2 text-amber-500 w-12">#</th>
                        <th className="text-start p-2 text-amber-500">{language === "ar" ? "الشهر" : "Month"}</th>
                        <th className="text-end p-2 text-amber-500">{language === "ar" ? "المبيعات" : "Sales"}</th>
                        <th className="text-end p-2 text-amber-500">{language === "ar" ? "المشتريات" : "Purchases"}</th>
                        <th className="text-end p-2 text-amber-500">{language === "ar" ? "المصاريف" : "Expenses"}</th>
                        <th className="text-end p-2 text-amber-500">{language === "ar" ? "الربح" : "Profit"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(report.monthly_data).map(([month, data], index) => (
                        <tr key={month} className="border-b border-slate-700/50">
                          <td className="p-2 text-center text-slate-400 font-mono">{index + 1}</td>
                          <td className="p-2 text-slate-300">{month}</td>
                          <td className="p-2 text-end text-green-500">{formatCurrency(data.sales)}</td>
                          <td className="p-2 text-end text-red-500">{formatCurrency(data.purchases)}</td>
                          <td className="p-2 text-end text-amber-500">{formatCurrency(data.expenses)}</td>
                          <td className={`p-2 text-end font-bold ${data.profit >= 0 ? "text-green-500" : "text-red-500"}`}>
                            {formatCurrency(data.profit)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Empty State */}
      {!report && !loading && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-12 text-center">
            <BarChart3 className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <p className="text-slate-400">
              {language === "ar" 
                ? "اختر الفترة واضغط على عرض التقرير" 
                : "Select period and click View Report"}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
